import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Cookie from "js-cookie";
import {
  ViewButton,
  DeleteButton,
  StyledButton,
  StyledButtonC,
  StyledAlert,
  StyledSnackbar,
  inputStyle,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { employeeStyles } from "components/Styles/employeeMasterStyles";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";

export default function RolesMaster() {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const [formData, setFormData] = useState({ roleId: 0, roleName: "" });
  const [formErrors, setFormErrors] = useState({});
  const [hrms_token] = useState(Cookie.get("hrms_token"));
  const [loading, setLoading] = useState(false);

  const MInput = inputStyle();

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsGetRoleDetails, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch roles");
      }

      const data = await response.json();
      setTableData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setAlert("Failed to fetch roles", "error");
      setTableData([]);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.roleName || !formData.roleName.trim()) {
      errors.roleName = "Role Name is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setAlert("Please fix the form errors", "error");
      return;
    }

    const payload = {
      roleId: isEditMode ? formData.roleId : 0,
      roleName: formData.roleName.trim(),
    };

    setLoading(true);
    try {
      const url = isEditMode
        ? `${URLs.hrmsGetRoleDetails}/${formData.roleId}`
        : URLs.hrmsGetRoleDetails;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || (isEditMode ? "Failed to update role" : "Failed to create role")
        );
      }

      setAlert(isEditMode ? "Role updated successfully" : "Role created successfully", "success");
      handleCancel();
      fetchRoles();
    } catch (error) {
      console.error("Error:", error);
      setAlert(error.message, "error");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${URLs.hrmsGetRoleDetails}/${deleteId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete role");
      }

      setAlert("Role deleted successfully", "success");
      setOpenDialog(false);
      fetchRoles();
    } catch (error) {
      console.error("Error:", error);
      setAlert(error.message, "error");
    }
    setLoading(false);
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const tabs = [{ label: "Roles List" }, { label: "Create Role" }];

  const handleCancel = () => {
    setFormData({ roleId: 0, roleName: "" });
    setFormErrors({});
    setIsEditMode(false);
    setActiveTab(0);
  };

  const handleReset = () => {
    setFormData({ roleId: 0, roleName: "" });
    setFormErrors({});
    setIsEditMode(false);
  };

  const ActionCellRenderer = (props) => {
    const rowIndex = props.node.rowIndex;
    const rowData = props.api.getDisplayedRowAtIndex(rowIndex).data;

    const handleEditClick = () => {
      setFormData({ roleId: rowData.roleId, roleName: rowData.roleName });
      setFormErrors({});
      setIsEditMode(true);
      setActiveTab(1);
    };

    const handleDeleteClick = () => {
      setDeleteId(rowData.roleId);
      setOpenDialog(true);
    };

    return (
      <div>
        <ViewButton type="button" onClick={handleEditClick}>
          <EditIcon />
        </ViewButton>
        <DeleteButton type="button" onClick={handleDeleteClick}>
          <DeleteIcon />
        </DeleteButton>
      </div>
    );
  };

  ActionCellRenderer.propTypes = {
    api: PropTypes.shape({
      getDisplayedRowAtIndex: PropTypes.func.isRequired,
    }).isRequired,
    node: PropTypes.shape({
      rowIndex: PropTypes.number.isRequired,
    }).isRequired,
  };

  const columnDefs = [
    {
      headerName: "Role Name",
      field: "roleName",
      sortable: true,
      filter: true,
      flex: 2,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      width: 120,
    },
  ];

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          flexGrow: 1,
          boxShadow: "none",
          border: "none",
          height: "100%",
          backgroundColor: "transparent",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Dialog open={openDialog}>
          <DialogContent sx={subDialogContentStyle}>
            <DialogContentText sx={dialogContentTextStyle}>
              Are you sure you want to delete this role?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={employeeStyles.dialogActions}>
            <StyledButton onClick={handleDelete}>Yes</StyledButton>
            <StyledButton onClick={() => setOpenDialog(false)} variant="outlined">
              No
            </StyledButton>
          </DialogActions>
        </Dialog>

        <StyledSnackbar
          open={open}
          autoHideDuration={5000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          severity={alertType}
        >
          <StyledAlert onClose={handleClose} severity={alertType}>
            {message}
          </StyledAlert>
        </StyledSnackbar>

        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
          sx={{
            width: "100%",
            "& .MuiTabs-flexContainer": {
              justifyContent: "flex-start",
              width: "100%",
            },
            "& .MuiTab-root": {
              minWidth: "250px",
              fontSize: "14px",
              fontWeight: "bold",
            },
          }}
        />

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            mt: 1,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
            width: "100%",
            height: "calc(100% - 48px)",
            overflow: "hidden",
          }}
        >
          {activeTab === 0 && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                borderRadius: "15px",
              }}
            >
              <StyledAgGridContainer
                component={Paper}
                className={`ag-theme-alpine ${employeeStyles.agTable}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "15px",
                }}
              >
                <AgGridReact
                  rowData={tableData}
                  columnDefs={columnDefs}
                  pagination={true}
                  paginationPageSize={11}
                />
              </StyledAgGridContainer>
            </Box>
          )}

          {activeTab === 1 && (
            <StyledTableContainer
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                backgroundColor: "#ffffff",
                width: "100%",
                height: "100%",
                borderRadius: "15px",
                p: 2,
                overflow: "hidden",
              }}
            >
              <Grid container spacing={1} sx={{ width: "100%", m: 0 }}>
                <Grid item xs={12}>
                  <div style={{ position: "relative", width: "100%" }}>
                    {isEditMode && (
                      <Tooltip title="Reset">
                        <StyledButtonC onClick={handleReset}>
                          <RestartAltIcon />
                        </StyledButtonC>
                      </Tooltip>
                    )}
                  </div>
                  <Grid sx={{ ...employeeStyles.root, p: 2 }}>
                    <form onSubmit={handleSubmit} autoComplete="off">
                      <Grid container spacing={2} sx={{ p: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Role Name"
                            name="roleName"
                            value={formData.roleName}
                            onChange={handleInputChange}
                            required
                            error={!!formErrors.roleName}
                            helperText={formErrors.roleName}
                            className={MInput.root}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 2 }}>
                            {isEditMode && (
                              <StyledButton onClick={handleCancel} variant="outlined">
                                Cancel
                              </StyledButton>
                            )}
                            <StyledButton type="submit" disabled={loading}>
                              {loading ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : isEditMode ? (
                                "Update"
                              ) : (
                                "Submit"
                              )}
                            </StyledButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </form>
                  </Grid>
                </Grid>
              </Grid>
            </StyledTableContainer>
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
